import { createEffect } from '@databases/selections';
import { type SelectionsApiContract } from '@databases/selections-api-types';

export interface CellEditingEffectDeps {
  selectionsApi: SelectionsApiContract;

  /**
   * Called when editing starts or stops on a cell.
   * `context` indicates where the edit is happening — 'table' or 'panel'.
   * On stop, `context` reflects where the edit was opened (used to close the right editor).
   */
  notifyCellEditing: (params: {
    entryId: string;
    fieldId: string;
    editing: boolean;
    context: 'table' | 'panel';
    initialKey?: string;
  }) => void;
}

export const createCellEditingEffect = createEffect(
  ({ selectionsApi, notifyCellEditing }: CellEditingEffectDeps) => {
    let openIds: { entryId: string; fieldId: string } | undefined;
    let openContext: 'table' | 'panel' = 'table';

    return {
      dispose() {
        if (openIds) {
          notifyCellEditing({ ...openIds, editing: false, context: openContext });
        }

        openIds = undefined;
      },

      deltaTypes: ['cellEditRequested', 'cellEditStopped'],

      onDelta(delta) {
        if (delta.kind === 'cellEditRequested') {
          const { entryId, fieldId } = selectionsApi.resolveCellEditPositionToIds(
            delta.position,
            delta.editingContext
          );

          if (!entryId || !fieldId) {
            return;
          }

          notifyCellEditing({
            entryId,
            fieldId,
            editing: true,
            context: delta.editingContext,
            initialKey: delta.initialKey,
          });

          openIds = { entryId, fieldId };
          openContext = delta.editingContext;
        } else if (delta.kind === 'cellEditStopped') {
          if (openIds) {
            notifyCellEditing({ ...openIds, editing: false, context: openContext });
          }

          openIds = undefined;
        }
      },
    };
  }
);
